import React from 'react';

import { SimpleImage } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/items.d.ts').EventListingItem}
 */
const EventListingItem = ({
    dateStart = '',
    dateEnd = '',
    eventLocation = null,
    eventType = null,
    externalCtaCssClass = '',
    externalUrl = '',
    facilitator = '',
    listingItemToExternalUrl = false,
    listingImage = null,
    listingText = '',
    mediaAsset = null,
    membersOnly = false,
    parent = null,
    priceText = '',
    title = '',
    url = '',
    whenText = ''
}) => {
    // misc
    const image = listingImage || mediaAsset?.image;

    const formatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    // render
    return (
        <div className='w-full bg-surface-raised rounded-listing-item expand-interaction hover:-translate-y-4 ease-linear duration-[80ms] transition-all card-detail'>
            <div className='h-full flex flex-wrap'>
                {/* Left Col - tablet */}
                <div className='w-full sm:w-6/8 flex flex-col gap-16 p-8 md:p-16'>
                    {/* Tag */}
                    {eventType?.length > 0 && (
                        <ul className='flex flex-wrap gap-8 items-start'>
                            {eventType.map((ev, idx) => {
                                return (
                                    <li key={idx}>
                                        <span className='tag-primary'>
                                            {ev.title}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className='h-full flex flex-col gap-8'>
                        {/* Image */}
                        {image && (
                            <figure className='rounded-lg overflow-hidden pointer-events-none select-none aspect-video relative'>
                                <div className='bg-[--tint-dark-10] w-full h-full absolute top-0 left-0 hidden card-detail--tint'/>
                                <SimpleImage
                                    {...image}
                                    className='h-full w-full object-cover object-center' />
                            </figure>
                        )}

                        {/* Title */}
                        <h3 className='type-style-sans-h3-med'>
                            {listingText || title}
                        </h3>

                        {/* Facilitator */}
                        {facilitator && (
                            <p className='type-style-sans-body'>
                                With {facilitator}
                            </p>
                        )}

                        {/* Link */}
                        <p className='type-style-sans-body mt-auto'>
                            {(externalUrl && listingItemToExternalUrl) ? (
                                <a
                                    className='mt-20 expand-interaction__action hover:underline hover:underline-offset-2'
                                    href={externalUrl}
                                    rel='noreferrer'
                                    target='_blank'>
                                    View Event
                                </a>
                            ) : (
                                <a
                                    className='mt-20 expand-interaction__action hover:underline hover:underline-offset-2'
                                    href={url}>
                                    View Event
                                </a>
                            )}
                        </p>
                    </div>
                </div>

                {/* Right Col - tablet */}
                <div className='w-full sm:w-2/8'>
                    <dl className='flex flex-col h-full sm:border-l sm:border-border-default'>
                        {/* Date */}
                        {whenText && (
                            <div className='flex-grow sm:h-1/3 px-8 py-10 md:p-16 border-t border-border-default sm:border-none'>
                                <dt className='sr-only sm:not-sr-only type-style-single-line-eyebrow'>
                                    When
                                </dt>

                                <dd className='type-style-listing-body sm:mt-8'>
                                    {whenText}
                                </dd>
                            </div>
                        )}

                        {/* Location */}
                        {(eventLocation?.length > 0) && (
                            <div className='flex-grow sm:h-1/3 px-8 py-10 md:p-16 border-t border-border-default'>
                                <dt className='sr-only sm:not-sr-only type-style-single-line-eyebrow'>
                                    Where
                                </dt>

                                <dd className='type-style-listing-body sm:mt-8'>
                                    <address className='not-italic'>
                                        {formatter.format(eventLocation.map(el => el.title))}
                                    </address>
                                </dd>
                            </div>
                        )}

                        {/* Tickets */}
                        {(externalUrl || membersOnly) && (
                            <div className='flex-grow sm:h-1/3 px-8 py-10 md:p-16 border-t border-border-default'>
                                <dt className='sr-only sm:not-sr-only type-style-single-line-eyebrow'>
                                    Tickets
                                </dt>

                                <dd className='type-style-listing-body sm:mt-8'>
                                    {membersOnly ? 'Members only' : priceText}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default EventListingItem;
